import React, { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useBlackoutsContext } from '../../PricingToolSessionContext';
import { SALESFORCE_FIELD_MAX_LENGTH, fieldLengthProps } from '../../constants';

import { validateBlackout } from './blackoutsValidation';
import type { BlackoutFormValues } from './types';

const EMPTY_FORM: BlackoutFormValues = { name: '', startDate: '', endDate: '' };

export default function BlackoutsTab() {
  const { blackouts, addBlackout, updateBlackout, removeBlackout } = useBlackoutsContext();
  const [form, setForm] = useState<BlackoutFormValues>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Array<string>>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const confirmDelete = () => {
    if (deleteId) {
      removeBlackout(deleteId);
    }
    setDeleteId(null);
  };

  const handleSubmit = () => {
    const validationErrors = validateBlackout(form);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (editingId) {
      updateBlackout(editingId, form);
    } else {
      addBlackout(form);
    }

    setForm(EMPTY_FORM);
    setEditingId(null);
    setErrors([]);
  };

  const handleEdit = (id: string) => {
    const draft = blackouts.find((item) => item.id === id);
    if (!draft) {
      return;
    }

    setForm({ name: draft.name, startDate: draft.startDate, endDate: draft.endDate });
    setEditingId(id);
    setErrors([]);
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setErrors([]);
  };

  return (
    <TabPanelShell
      subtitle="Blackout date ranges are kept in this session until Save Session is wired."
      title="Blackouts"
    >
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography fontWeight={600} variant="subtitle1">
                {editingId ? 'Edit blackout' : 'Add blackout'}
              </Typography>
              <TextField
                fullWidth
                {...fieldLengthProps(form.name, SALESFORCE_FIELD_MAX_LENGTH.blackoutName)}
                label="Name"
                size="small"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Start date"
                  size="small"
                  type="date"
                  value={form.startDate}
                  onChange={(event) => setForm((prev) => ({ ...prev, startDate: event.target.value }))}
                />
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="End date"
                  size="small"
                  type="date"
                  value={form.endDate}
                  onChange={(event) => setForm((prev) => ({ ...prev, endDate: event.target.value }))}
                />
              </Stack>
              {errors.length > 0 && (
                <Alert severity="error" variant="outlined">
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {errors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </Box>
                </Alert>
              )}
              <Stack direction="row" spacing={1}>
                <Button onClick={handleSubmit} size="small" variant="contained">
                  {editingId ? 'Update' : 'Add'}
                </Button>
                {editingId && (
                  <Button onClick={handleCancel} size="small" variant="text">
                    Cancel
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {blackouts.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No blackout ranges yet.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {blackouts.map((draft) => (
              <Card key={draft.id} variant="outlined">
                <CardContent>
                  <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography fontWeight={600} variant="subtitle2">
                        {draft.name}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {draft.startDate} → {draft.endDate}
                      </Typography>
                    </Box>
                    <Stack direction="row">
                      <IconButton aria-label="Edit blackout" onClick={() => handleEdit(draft.id)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton aria-label="Delete blackout" onClick={() => setDeleteId(draft.id)} size="small">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete blackout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this blackout period? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </TabPanelShell>
  );
}
